import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Where someone connects their own Figma, Confluence and Jira accounts.
 *
 * What gets connected here is used only for turns this person starts: when
 * someone else in the room prompts, Claude reaches those services with that
 * person's account, or not at all. The screen says so, because in a shared
 * room "connected" could otherwise be read as "connected for everyone".
 *
 * Lives as a tab of the settings dialog, which owns the scrim, Escape and
 * the close button.
 */

interface Connection {
  provider: string;
  name: string;
  description: string;
  auth: 'oauth' | 'token';
  tokenUrl?: string;
  tokenHelp?: string;
  connected: boolean;
  account?: string;
  connectedAt?: number;
  lastUsedAt?: number;
  broken: boolean;
  brokenReason?: string;
}

interface Props {
  /** Lets the rail's dot follow along without refetching. */
  onChange?: (connections: Connection[]) => void;
}

async function api(path: string, init?: RequestInit) {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || body.error || `Request failed (${res.status})`);
  return body;
}

/** An OAuth popup gets this long before we stop watching for it. */
const OAUTH_WATCH_MS = 5 * 60 * 1000;

export function ConnectionsPanel({ onChange }: Props) {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [connections, setConnections] = useState<Connection[]>([]);

  /** Which token form is open, and what is typed in it. */
  const [tokenFor, setTokenFor] = useState('');
  const [token, setToken] = useState('');

  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const watchRef = useRef<number | undefined>(undefined);

  const apply = useCallback(
    (next: Connection[]) => {
      setConnections(next);
      onChange?.(next);
    },
    [onChange],
  );

  const refresh = useCallback(
    () =>
      api('/api/mcp/connections').then((body) => {
        setAvailable(body.available !== false);
        apply(body.connections ?? []);
        return body.connections as Connection[];
      }),
    [apply],
  );

  useEffect(() => {
    refresh()
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [refresh]);

  // The OAuth popup reports back when it can. It cannot always: a provider
  // that sets Cross-Origin-Opener-Policy severs window.opener, so a poll
  // below covers that case.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; ok?: boolean; provider?: string } | null;
      if (data?.type !== 'cowork-mcp') return;
      stopWatching();
      void refresh().then(() => {
        if (data.ok) setNote('Bağlandı.');
      });
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      stopWatching();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  function stopWatching() {
    if (watchRef.current !== undefined) window.clearInterval(watchRef.current);
    watchRef.current = undefined;
    setBusy((b) => (b.startsWith('oauth:') ? '' : b));
  }

  function run(label: string, fn: () => Promise<void>) {
    setBusy(label);
    setError('');
    setNote('');
    fn()
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy((b) => (b === label ? '' : b)));
  }

  const saveToken = (provider: string) =>
    run(`token:${provider}`, async () => {
      const body = await api(`/api/mcp/connections/${provider}/token`, {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      apply(body.connections);
      setToken('');
      setTokenFor('');
      setNote('Bağlandı.');
    });

  const connectOAuth = (provider: string) => {
    // Opened before the request, synchronously inside the click: a window
    // opened after an await is a popup the browser is entitled to block.
    const popup = window.open('about:blank', `cowork-mcp-${provider}`, 'width=560,height=720');
    setBusy(`oauth:${provider}`);
    setError('');
    setNote('');
    api(`/api/mcp/connections/${provider}/oauth`, { method: 'POST' })
      .then((body: { url: string }) => {
        if (popup && !popup.closed) popup.location.href = body.url;
        else window.location.href = body.url;

        const startedAt = Date.now();
        const before = connections.find((c) => c.provider === provider)?.connectedAt;
        watchRef.current = window.setInterval(() => {
          if (Date.now() - startedAt > OAUTH_WATCH_MS) return stopWatching();
          void refresh().then((list) => {
            const now = list.find((c) => c.provider === provider);
            if (now?.connected && !now.broken && now.connectedAt !== before) {
              stopWatching();
              setNote('Bağlandı.');
            } else if (popup?.closed) {
              stopWatching();
            }
          });
        }, 2000);
      })
      .catch((e: Error) => {
        popup?.close();
        setError(e.message);
        setBusy('');
      });
  };

  const test = (provider: string) =>
    run(`test:${provider}`, async () => {
      const body = await api(`/api/mcp/connections/${provider}/test`, { method: 'POST' });
      if (body.connections) apply(body.connections);
      if (body.ok) setNote(body.message);
      else setError(body.message);
    });

  const remove = (provider: string) =>
    run(`remove:${provider}`, async () => {
      const body = await api(`/api/mcp/connections/${provider}`, { method: 'DELETE' });
      apply(body.connections);
    });

  const connect = (c: Connection) => {
    if (c.auth === 'oauth') connectOAuth(c.provider);
    else {
      setTokenFor(c.provider);
      setToken('');
      setError('');
    }
  };

  return (
    <>
      <div className="share__head settings__head">
        <div>
          <div className="share__title" id="connections-title">
            Bağlantılar
          </div>
          <div className="share__sub">
            Claude, senin başlattığın turlarda bu servislere senin hesabınla erişir.
          </div>
        </div>
      </div>

      <div className="gitkey__body">
        {loading && <p className="share__empty">Yükleniyor…</p>}

        {!loading && !available && (
          <p className="gitkey__warn">
            Sunucuda <code>KEY_ENCRYPTION_SECRET</code> tanımlı değil, bu yüzden bağlantı
            bilgileri saklanamıyor. Yöneticiye söyle.
          </p>
        )}

        {!loading &&
          available &&
          connections.map((c) => (
            <div className="gitkey__card" key={c.provider}>
              <div className="gitkey__row">
                <strong>{c.name}</strong>
                <span
                  className={`gitkey__badge ${c.connected && !c.broken ? 'gitkey__badge--ok' : ''}`}
                >
                  {!c.connected ? 'bağlı değil' : c.broken ? 'yeniden bağlan' : 'bağlı'}
                </span>
              </div>
              <div className="gitkey__meta">
                {c.connected && c.account ? `${c.account} olarak bağlı` : c.description}
                {c.connected && c.lastUsedAt &&
                  ` · son kullanım: ${new Date(c.lastUsedAt).toLocaleDateString()}`}
              </div>

              {c.broken && (
                <p className="gitkey__warn">
                  {c.name} bu bağlantıyı artık kabul etmiyor
                  {c.auth === 'token' ? ' (token iptal edilmiş ya da süresi dolmuş olabilir)' : ''}.
                  Yeniden bağlan.
                </p>
              )}

              {tokenFor === c.provider && (
                <>
                  {c.tokenHelp && <p className="gitkey__hint">{c.tokenHelp}</p>}
                  {c.tokenUrl && (
                    <p className="gitkey__hint">
                      <a href={c.tokenUrl} target="_blank" rel="noreferrer">
                        {c.name} ayarlarını aç ↗
                      </a>
                    </p>
                  )}
                  <input
                    className="share__filter"
                    type="password"
                    autoFocus
                    spellCheck={false}
                    placeholder="figd_…"
                    value={token}
                    onChange={(e) => setToken(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && token.trim()) saveToken(c.provider);
                    }}
                  />
                </>
              )}

              <div className="gitkey__actions">
                {tokenFor === c.provider ? (
                  <>
                    <button
                      className="gitkey__btn gitkey__btn--primary"
                      onClick={() => saveToken(c.provider)}
                      disabled={!!busy || !token.trim()}
                    >
                      {busy === `token:${c.provider}` ? 'Doğrulanıyor…' : 'Kaydet'}
                    </button>
                    <button className="gitkey__btn" onClick={() => setTokenFor('')} disabled={!!busy}>
                      Vazgeç
                    </button>
                  </>
                ) : (
                  <>
                    {(!c.connected || c.broken) && (
                      <button
                        className="gitkey__btn gitkey__btn--primary"
                        onClick={() => connect(c)}
                        disabled={!!busy}
                      >
                        {busy === `oauth:${c.provider}`
                          ? 'Onay bekleniyor…'
                          : c.broken
                            ? 'Yeniden bağlan'
                            : 'Bağlan'}
                      </button>
                    )}
                    {c.connected && !c.broken && (
                      <button className="gitkey__btn" onClick={() => test(c.provider)} disabled={!!busy}>
                        {busy === `test:${c.provider}` ? 'Deneniyor…' : 'Test et'}
                      </button>
                    )}
                    {c.connected && (
                      <button className="gitkey__btn" onClick={() => remove(c.provider)} disabled={!!busy}>
                        Bağlantıyı kes
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

        {error && <p className="gitkey__error">{error}</p>}
        {note && <p className="gitkey__note">{note}</p>}
      </div>

      <div className="share__foot">
        Bilgilerin sunucuda şifreli durur ve tarayıcıya geri dönmez. Odadaki başkası prompt
        yazdığında senin hesabın kullanılmaz; yazma yapan işlemleri yalnız sen onaylayabilirsin.
      </div>
    </>
  );
}

export type { Connection as McpConnection };
